import chalk from 'chalk';
import { existsSync, realpathSync } from 'node:fs';
import { join } from 'node:path';
import { homedir, userInfo } from 'node:os';
import { spawn } from 'node:child_process';
import { getApiKey, getHost } from '../lib/config.js';
import { startWatchdog, stopWatchdog, getManagerStatus } from '../lib/watchdog.js';
import { success, error, info, table } from '../lib/output.js';
import { isJsonMode, jsonOutput } from '../lib/globals.js';
import { normalizePinVersion } from '../lib/self-update-pin.js';

/**
 * ENG-7833: only forward a VALID pin into the supervisor/unit env. An invalid
 * AGT_CLI_PIN_VERSION must not reach the unit, or the ExecStartPre repair would
 * bake it into `npm install -g …@<bad>` and loop-fail, blocking the manager from
 * starting. Drop it so repair falls back to the release channel; a valid pin is
 * normalized (leading `v` stripped) to match what the self-updater installs.
 */
function normalizePinInEnv(env: Record<string, string>): void {
  const normalized = normalizePinVersion(env.AGT_CLI_PIN_VERSION);
  if (normalized) env.AGT_CLI_PIN_VERSION = normalized;
  else delete env.AGT_CLI_PIN_VERSION;
}

// ---------------------------------------------------------------------------
// agt manager start
// ---------------------------------------------------------------------------

interface ManagerStartOptions {
  interval?: string;
  configDir?: string;
  supervise?: boolean;
}

export function managerStartCommand(opts: ManagerStartOptions): void {
  const json = isJsonMode();

  // ENG-4632: when the manager is launched without a user shell session
  // (e.g. via `aws ssm send-command`, systemd without User=, or a
  // bare-metal init script), HOME and USER may be missing from the
  // process env. Every agent the manager spawns under tmux inherits
  // that env — and Claude Code without HOME can't resolve
  // ~/.claude/.credentials.json, so the agent silently falls back to
  // the interactive login picker and never spawns its MCP servers.
  // This was the root cause of the prod scout outage on 2026-05-01.
  // Backfill from os primitives before the watchdog/spawn layer reads
  // the env. We log a warning so operators can still see the underlying
  // misconfiguration — silent recovery would just hide the next
  // regression.
  // Treat empty-string as missing — HOME="" makes ~ resolve to cwd,
  // which fails the same way as no HOME but is harder to diagnose.
  if (!process.env.HOME || !process.env.HOME.trim()) {
    const fallback = homedir();
    process.env.HOME = fallback;
    if (!json) {
      info(`HOME was not set in the manager env — defaulting to ${fallback}.`);
      info('  This usually means the manager was launched without a user shell session.');
      info('  For reboot survival, install the supervisor: agt manager install');
    }
  }
  if (!process.env.USER || !process.env.USER.trim()) {
    const fallback = userInfo().username;
    process.env.USER = fallback;
    if (!json) info(`USER was not set in the manager env — defaulting to ${fallback}.`);
  }

  const apiKey = getApiKey();
  if (!apiKey) {
    const msg = 'AGT_API_KEY is not set. Export it with your host API key (tlk_...)';
    if (json) { jsonOutput({ ok: false, error: msg }); } else { error(msg); }
    process.exitCode = 1;
    return;
  }

  const intervalSec = parseInt(opts.interval ?? '10', 10);
  if (isNaN(intervalSec) || intervalSec < 5) {
    if (json) {
      jsonOutput({ ok: false, error: 'Interval must be at least 10 seconds' });
    } else {
      error('Interval must be at least 10 seconds.');
    }
    process.exitCode = 1;
    return;
  }

  const configDir = opts.configDir ?? join(homedir(), '.augmented');

  // ENG-4488: --supervise runs the manager in a respawn loop. The manager
  // signals "please restart me" by exiting with SUPERVISOR_RESTART_EXIT_CODE
  // (75); only that exact code triggers a respawn. Exit 0 is a normal
  // graceful stop (e.g. from `agt manager stop` / SIGTERM) and causes the
  // supervisor to exit too. Any other non-zero code is propagated out so
  // tmux/launchctl/etc. see the failure. This is the loop that makes
  // auto-upgrade transparent: after `brew upgrade` the manager exits 75,
  // the supervisor re-runs `agt manager start` → new Cellar version loads.
  if (opts.supervise) {
    // Supervisor emits human-readable [supervisor] lines to stdout and the
    // child inherits stdio — not compatible with --json's one-shot JSON
    // contract. Reject the combination loudly rather than silently
    // corrupting a machine-parseable stream.
    if (json) {
      jsonOutput({ ok: false, error: '--supervise is not supported with --json' });
      process.exitCode = 1;
      return;
    }
    runSupervisorLoop(intervalSec, configDir);
    return;
  }

  try {
    const { pid } = startWatchdog({
      intervalMs: intervalSec * 1000,
      configDir,
    });

    if (json) {
      jsonOutput({ ok: true, pid, interval: intervalSec, configDir });
    } else {
      success(`Manager started (PID ${pid}, interval ${intervalSec}s)`);
      info(`Config dir: ${configDir}`);
      info('Stop with: agt manager stop');
    }
  } catch (err) {
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

/**
 * Respawn-on-restart-code loop for `agt manager start --supervise`. Each
 * iteration runs the manager as a child process until it exits; only
 * SUPERVISOR_RESTART_EXIT_CODE triggers a re-spawn. Exit 0 is treated as
 * a true stop so `agt manager stop` actually stops a supervised manager.
 * Any other non-zero exit propagates out so an external watchdog can see
 * the failure rather than the supervisor masking it as a crash loop.
 * Lives at the command layer (not watchdog) so it stays outside the
 * manager's own ESM graph — the whole point is that `brew upgrade` will
 * replace that graph on disk between iterations.
 */
// Dedicated exit code the manager uses to signal "restart me" to the
// supervisor (e.g. after a successful `brew upgrade`). Must not collide
// with Node's default codes (0 = clean stop, 1 = generic error, 128+sig).
// 75 = EX_TEMPFAIL in sysexits.h, which is semantically close to
// "temporary failure — please retry" and doesn't shadow anything else
// the CLI uses. Exported so manager-worker and this supervisor share
// exactly one constant — overloading exit code 0 conflated `agt manager
// stop` with auto-upgrade restart, so the supervisor would keep respawning.
export const SUPERVISOR_RESTART_EXIT_CODE = 75;

function runSupervisorLoop(intervalSec: number, configDir: string): void {
  const SUPERVISOR_RESPAWN_DELAY_MS = 2_000;
  const stdoutWrite = (line: string) => process.stdout.write(`${line}\n`);

  // Supervisor-level state — one set of signal handlers for the whole
  // process. Per-child handlers accumulate on every respawn and leave gaps
  // during the backoff window where a kill would not propagate to anything.
  let currentChild: ReturnType<typeof spawn> | null = null;
  let respawnTimer: ReturnType<typeof setTimeout> | null = null;
  // Flips when the supervisor receives a shutdown signal. Without this,
  // forwarding SIGTERM to the child makes the child exit 0 (graceful stop),
  // which looks identical to the auto-upgrade clean-restart case and the
  // exit handler would respawn — so `agt manager start --supervise` could
  // never actually be stopped by SIGTERM/SIGINT.
  let shutdownRequested = false;

  const forwardOrExit = (sig: NodeJS.Signals) => (): void => {
    shutdownRequested = true;
    // Cancel any pending respawn first so we don't race a new child into
    // existence after the operator asked us to stop.
    if (respawnTimer) {
      clearTimeout(respawnTimer);
      respawnTimer = null;
    }
    if (currentChild && currentChild.exitCode === null) {
      // .killed just indicates a signal was sent, not that the child has
      // exited. A second Ctrl-C during graceful shutdown must forward
      // again so the operator can escalate to hard-kill the child; it
      // must NOT fall through to process.exit(0) with the child still
      // running, which would orphan it outside the supervisor.
      currentChild.kill(sig);
      return;
    }
    // Child already exited (we're in the 2s backoff) — nothing to kill,
    // just stop the supervisor.
    process.exit(0);
  };

  process.on('SIGTERM', forwardOrExit('SIGTERM'));
  process.on('SIGINT', forwardOrExit('SIGINT'));

  const runOne = (): void => {
    respawnTimer = null;
    currentChild = spawn(
      process.execPath,
      [process.argv[1]!, 'manager', 'start', '--interval', String(intervalSec), '--config-dir', configDir],
      { stdio: 'inherit', env: process.env },
    );
    // Without this, a spawn failure (missing Node binary, permission
    // denied, etc.) crashes the supervisor without running any of the
    // exit bookkeeping below — operator sees no log, tmux session just
    // disappears. Log and exit 1 deterministically.
    currentChild.once('error', (err) => {
      currentChild = null;
      stdoutWrite(`[supervisor] failed to spawn manager: ${err.message}`);
      process.exit(1);
    });
    currentChild.on('exit', (code, signal) => {
      currentChild = null;
      if (shutdownRequested) {
        // Operator asked us to stop via SIGTERM/SIGINT; the child's exit
        // (code 0 on graceful stop, or non-zero on crash mid-shutdown)
        // does not re-enter the respawn path.
        stdoutWrite('[supervisor] shutdown requested — exiting');
        process.exit(0);
        return;
      }
      if (signal) {
        stdoutWrite(`[supervisor] manager terminated by signal ${signal} — exiting`);
        process.exit(1);
        return;
      }
      if (code === SUPERVISOR_RESTART_EXIT_CODE) {
        stdoutWrite(`[supervisor] manager requested restart (exit ${code}) — respawning in ${SUPERVISOR_RESPAWN_DELAY_MS / 1000}s`);
        respawnTimer = setTimeout(runOne, SUPERVISOR_RESPAWN_DELAY_MS);
        return;
      }
      if (code === 0) {
        // Normal graceful stop (e.g. `agt manager stop` sent SIGTERM, the
        // child drained, then exited 0). Do NOT respawn — that would make
        // `agt manager stop` effectively useless against a supervised
        // manager. Only the dedicated restart code triggers respawn.
        stdoutWrite('[supervisor] manager exited cleanly (no restart requested) — exiting');
        process.exit(0);
        return;
      }
      stdoutWrite(`[supervisor] manager exited with code ${code} — not respawning`);
      process.exit(code ?? 1);
    });
  };

  stdoutWrite(`[supervisor] starting manager with respawn-on-restart-code=${SUPERVISOR_RESTART_EXIT_CODE} (interval=${intervalSec}s, configDir=${configDir})`);
  runOne();
}

// ---------------------------------------------------------------------------
// agt manager stop
// ---------------------------------------------------------------------------

interface ManagerCommonOptions {
  configDir?: string;
}

export async function managerStopCommand(opts: ManagerCommonOptions = {}): Promise<void> {
  const json = isJsonMode();
  const configDir = opts.configDir ?? join(homedir(), '.augmented');

  try {
    const result = await stopWatchdog(configDir);

    if (!result.stopped && !result.pid) {
      if (json) {
        jsonOutput({ ok: false, error: 'Manager is not running' });
      } else {
        error('Manager is not running.');
      }
      process.exitCode = 1;
      return;
    }

    if (json) {
      jsonOutput({ ok: true, stopped: true, pid: result.pid });
    } else {
      success(`Manager stopped (PID ${result.pid})`);
    }
  } catch (err) {
    if (json) {
      jsonOutput({ ok: false, error: (err as Error).message });
    } else {
      error((err as Error).message);
    }
    process.exitCode = 1;
  }
}

// ---------------------------------------------------------------------------
// agt manager status
// ---------------------------------------------------------------------------

export function managerStatusCommand(opts: ManagerCommonOptions = {}): void {
  const json = isJsonMode();
  const configDir = opts.configDir ?? join(homedir(), '.augmented');

  const status = getManagerStatus(configDir);

  if (!status) {
    if (json) {
      jsonOutput({ ok: true, running: false });
    } else {
      info('Manager is not running.');
    }
    return;
  }

  if (json) {
    jsonOutput({ ok: true, running: true, ...status });
    return;
  }

  console.log(chalk.bold('\nManager Status\n'));

  info(`PID:        ${status.pid}`);
  info(`Started:    ${status.startedAt}`);
  info(`Last poll:  ${status.lastPollAt ?? chalk.dim('none')}`);
  info(`Polls:      ${status.pollCount}`);
  info(`Errors:     ${status.errorCount}`);
  console.log();

  if (status.agents.length === 0) {
    info('No agents discovered yet.');
    return;
  }

  const rows = status.agents.map((a) => {
    let gwStatus = chalk.dim('—');
    if (a.gatewayRunning) {
      gwStatus = chalk.green(`:${a.gatewayPort} (PID ${a.gatewayPid})`);
    } else if (a.gatewayPort) {
      gwStatus = chalk.red(`:${a.gatewayPort} (down)`);
    }

    return [
      a.codeName,
      a.status === 'active' ? chalk.green(a.status) : a.status === 'paused' ? chalk.yellow(a.status) : chalk.dim(a.status ?? '—'),
      a.charterVersion || chalk.dim('—'),
      gwStatus,
      a.lastProvisionAt ? new Date(a.lastProvisionAt).toLocaleTimeString() : chalk.dim('—'),
      a.lastDriftCheckAt ? new Date(a.lastDriftCheckAt).toLocaleTimeString() : chalk.dim('—'),
    ];
  });

  table(
    ['Agent', 'Status', 'Charter', 'Gateway', 'Last Provision', 'Last Drift'],
    rows,
  );

  // Show ACP sessions if any agent has active ones
  const acpAgents = status.agents.filter((a) => a.acpSessions && a.acpSessions.length > 0);
  if (acpAgents.length > 0) {
    console.log(chalk.bold('\nACP Sessions\n'));
    const acpRows = acpAgents.flatMap((a) =>
      a.acpSessions.map((s) => [
        a.codeName,
        s.agentCommand,
        s.sessionName ?? chalk.dim('default'),
        s.queueState === 'running' ? chalk.green(s.queueState) : s.queueState === 'queued' ? chalk.yellow(s.queueState) : chalk.dim(s.queueState),
        String(s.turnCount),
        new Date(s.startedAt).toLocaleTimeString(),
      ]),
    );
    table(
      ['Agent', 'Coding Agent', 'Session', 'Queue', 'Turns', 'Started'],
      acpRows,
    );
  }
}

/**
 * Replace a versioned Homebrew Cellar path with the stable
 * `<prefix>/bin/agt` symlink so the launchd plist survives upgrades.
 *
 *   /opt/homebrew/Cellar/agt/0.15.36/bin/agt.js
 *     → /opt/homebrew/bin/agt   (if that exists)
 *
 * Returns the input untouched when:
 *   - The path is not inside a Cellar (npm-global, dev, etc.).
 *   - The expected `<prefix>/bin/agt` symlink doesn't resolve.
 *
 * Exported for testing.
 */
/**
 * Environment the supervised manager needs at launch, carried from the shell
 * that runs the installer into the systemd unit body.
 *
 * ONE LIST, BOTH INSTALLERS. `agt manager install` (--user) and
 * `agt manager install-system-unit` (root) each built this inline, so a key
 * added for one lane was silently missing from the other — and the two lanes
 * are reached by the same operator on the same host under different
 * circumstances. A key that reaches only one of them is a key you cannot rely
 * on having.
 *
 * What is here and why:
 *   - AGT_TEAM — the manager's active team.
 *   - AGT_CLI_RELEASE_CHANNEL — self-update dist-tag ('test' on test hosts).
 *   - AGT_CLI_PIN_VERSION / AGT_CLI_PIN_ALLOW_URGENT (ENG-7833) — version pin,
 *     honoured by both the self-update and the unit's ExecStartPre repair.
 *   - AGT_ISOLATION / AGT_ISOLATION_AGENTS / AGT_ISOLATION_IMAGE (ENG-10696) —
 *     whether agents on this host run in containers at all. The EC2 bootstrap
 *     exports AGT_ISOLATION before invoking the installer; absent from this
 *     list, that export is dropped and the host runs unisolated with nothing
 *     reporting the gap.
 *   - PATH / CLAUDE_PATH — binary resolution for the spawned manager.
 *
 * AGT_ISOLATION_MEMORY is deliberately absent. Per-agent memory ceilings come
 * from `agent_class` since ENG-10641; that variable is the host-wide override
 * that flattens them back to a single number, and baking it into a unit makes
 * the override permanent.
 */
export const SUPERVISOR_ENV_PASSTHROUGH_KEYS = [
  'AGT_TEAM',
  'AGT_CLI_RELEASE_CHANNEL',
  'AGT_CLI_PIN_VERSION',
  'AGT_CLI_PIN_ALLOW_URGENT',
  'AGT_ISOLATION',
  'AGT_ISOLATION_AGENTS',
  'AGT_ISOLATION_IMAGE',
  'PATH',
  'CLAUDE_PATH',
] as const;

/**
 * Copy the passthrough keys that are actually set in `source` into `env`.
 * Unset keys are omitted rather than written as empty strings: an empty
 * `Environment="AGT_ISOLATION="` in the unit reads as an explicit "not docker",
 * which is the same value but a different statement, and it would mask a shell
 * that legitimately had nothing to say.
 */
export function applySupervisorPassthroughEnv(
  env: Record<string, string>,
  source: NodeJS.ProcessEnv = process.env,
): Record<string, string> {
  for (const k of SUPERVISOR_ENV_PASSTHROUGH_KEYS) {
    const v = source[k];
    if (v != null) env[k] = v;
  }
  return env;
}

export function resolveStableAgtBin(rawPath: string): string {
  // Match `<prefix>/Cellar/<formula>/<version>/...` — works for both
  // `/opt/homebrew/Cellar` and `/home/linuxbrew/.linuxbrew/Cellar`.
  const match = rawPath.match(/^(.*?)\/Cellar\/([^/]+)\/[^/]+\//);
  if (!match) return rawPath;
  const prefix = match[1];
  const formula = match[2];
  if (!prefix || !formula) return rawPath;

  // brew links the formula's bin entries under `<prefix>/bin/<name>`.
  // The convention for our tap is the formula name itself (`agt`), but
  // be defensive: try the formula name first, fall back to literal `agt`.
  // Resolve via realpathSync so we still detect the symlink as broken
  // when its target is missing (the exact failure mode this fix exists
  // to prevent — we'd otherwise happily write a path that ENOENTs).
  const candidates = [`${prefix}/bin/${formula}`, `${prefix}/bin/agt`];
  for (const candidate of candidates) {
    if (!existsSync(candidate)) continue;
    try {
      // realpath confirms the symlink resolves to a real file. If brew
      // is mid-upgrade and the symlink is dangling, fall through.
      realpathSync(candidate);
      return candidate;
    } catch { /* dangling symlink — try the next candidate */ }
  }
  return rawPath;
}

// ---------------------------------------------------------------------------
// agt manager install / uninstall — OS-level supervisor (ENG-4593)
// ---------------------------------------------------------------------------

interface ManagerInstallOptions {
  interval?: string;
  configDir?: string;
}

export async function managerInstallCommand(opts: ManagerInstallOptions = {}): Promise<void> {
  const json = isJsonMode();
  const { installSupervisor, supervisorStatus } = await import('../lib/manager-supervisor.js');

  const intervalSec = parseInt(opts.interval ?? '10', 10);
  if (isNaN(intervalSec) || intervalSec < 5) {
    const msg = 'Interval must be at least 5 seconds.';
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }

  const configDir = opts.configDir ?? join(homedir(), '.augmented');

  // Resolve the agt binary the supervisor will launch. process.argv[1]
  // is the entry point of the currently-running agt, but on Homebrew
  // installs that's the *versioned* Cellar path
  // (e.g. /opt/homebrew/Cellar/agt/0.15.36/bin/agt.js). brew upgrade
  // deletes that exact directory once the new version is staged, so the
  // launchd plist would point at a path that vanishes on the first
  // self-update — launchd then throttles into a permanent ENOENT loop
  // and the manager never comes back. Promote to the stable
  // `<prefix>/bin/agt` symlink that brew keeps pointing at the current
  // version. npm-global installs already live at a stable path
  // (`<prefix>/lib/node_modules/.../bin/agt.js`) that npm overwrites
  // in place, so the resolver leaves those untouched.
  const rawAgtBin = process.argv[1];
  if (!rawAgtBin) {
    const msg = 'Could not resolve the agt binary path from argv. Re-run via the installed `agt` command.';
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }
  const agtBin = resolveStableAgtBin(rawAgtBin);

  // macOS TCC sandboxes launchd-spawned processes — they EPERM on
  // reads under user folders like Documents/Downloads/Desktop/etc.
  // The install would succeed and the manager would crash on first
  // launch with no operator-actionable signal. Refuse here instead.
  if (process.platform === 'darwin') {
    const home = homedir();
    const protectedRoots = ['Documents', 'Downloads', 'Desktop', 'Movies', 'Music', 'Pictures'];
    const offending = protectedRoots
      .map((r) => join(home, r))
      .find((p) => agtBin === p || agtBin.startsWith(`${p}/`));
    if (offending) {
      const msg = `agt binary at ${agtBin} sits inside a macOS TCC-protected folder (${offending}). launchd-spawned processes cannot read files there and the manager would EPERM on startup. Either install agt globally (\`npm install -g @integrity-labs/agt-cli\`) or copy the dist outside protected folders before running this command.`;
      if (json) jsonOutput({ ok: false, error: msg });
      else error(msg);
      process.exitCode = 1;
      return;
    }
  }

  // AGT_HOST must go through getHost() so the CLI's production-default
  // fallback applies — without it, an install on a host that hasn't
  // exported AGT_HOST in the shell would pass an empty value through
  // to launchd and the manager would fail to find the API.
  //
  // ENG-4632: HOME and USER are required so Claude Code in spawned
  // agent sessions can resolve ~/.claude/.credentials.json. Bake the
  // operator's HOME/USER into the unit/plist explicitly — relying on
  // launchd / systemd defaults left at least one prod host with a
  // PATH-only env and silently broken agents.
  const env: Record<string, string> = {
    AGT_HOST: getHost(),
    // ?? alone wouldn't catch `HOME=""` from a stripped systemd env.
    // Treat empty / whitespace-only as missing.
    HOME: (process.env.HOME?.trim()) || homedir(),
    USER: (process.env.USER?.trim()) || userInfo().username,
  };
  const apiKey = getApiKey();
  if (apiKey) env.AGT_API_KEY = apiKey;
  // AGT_CLI_RELEASE_CHANNEL gates the self-update channel ('test' on the
  // test environment's hosts). Carry it through so the supervised manager's
  // self-update tracks the right npm dist-tag; unset ⇒ the manager defaults
  // to 'latest', unchanged for prod hosts.
  // ENG-7833: AGT_CLI_PIN_VERSION / AGT_CLI_PIN_ALLOW_URGENT pin the host to an
  // exact version, so carry them through too so both the manager's self-update and
  // the unit's ExecStartPre repair honor the pin.
  applySupervisorPassthroughEnv(env);
  normalizePinInEnv(env);

  const result = await installSupervisor({ agtBin, intervalSec, configDir, env });
  if (!result.ok) {
    if (json) jsonOutput({ ok: false, error: result.error });
    else error(result.error);
    process.exitCode = 1;
    return;
  }

  const status = supervisorStatus();
  if (json) {
    jsonOutput({ ok: true, status, details: result.details });
    return;
  }
  success('Supervisor installed.');
  info(result.details);
  if (status.kind === 'installed' && status.pid != null) {
    info(`Manager already running under the supervisor — PID ${status.pid}.`);
  }
}

export async function managerUninstallCommand(): Promise<void> {
  const json = isJsonMode();
  const { uninstallSupervisor } = await import('../lib/manager-supervisor.js');

  const result = await uninstallSupervisor();
  if (!result.ok) {
    if (json) jsonOutput({ ok: false, error: result.error });
    else error(result.error);
    process.exitCode = 1;
    return;
  }
  if (json) jsonOutput({ ok: true, details: result.details });
  else {
    success('Supervisor uninstalled.');
    info(result.details);
  }
}

// ---------------------------------------------------------------------------
// agt manager install-system-unit / uninstall-system-unit (ENG-4706)
// ---------------------------------------------------------------------------
//
// Sibling of `agt manager install` that targets a system-level systemd
// unit at /etc/systemd/system/agt-manager.service. Used by the EC2
// host-bootstrap (host-bootstrap.ts) and by the SSM backfill runbook
// for existing hosts. Requires root — the --user variant is for local
// dev and doesn't survive headless reboot.

interface ManagerInstallSystemUnitOptions {
  interval?: string;
  configDir?: string;
  user?: string;
}

export async function managerInstallSystemUnitCommand(
  opts: ManagerInstallSystemUnitOptions = {},
): Promise<void> {
  const json = isJsonMode();
  const { installSystemUnit, systemUnitStatus } = await import('../lib/manager-supervisor.js');

  const intervalSec = parseInt(opts.interval ?? '10', 10);
  if (isNaN(intervalSec) || intervalSec < 5) {
    const msg = 'Interval must be at least 5 seconds.';
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }

  const user = opts.user ?? 'root';
  const configDir = opts.configDir ?? (user === 'root' ? '/root/.augmented' : join('/home', user, '.augmented'));

  const rawAgtBin = process.argv[1];
  if (!rawAgtBin) {
    const msg = 'Could not resolve the agt binary path from argv. Re-run via the installed `agt` command.';
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }
  const agtBin = resolveStableAgtBin(rawAgtBin);

  // System unit only makes sense on Linux. The wrapper enforces this
  // too, but failing fast in the CLI surface gives a cleaner error.
  if (process.platform !== 'linux') {
    const msg = `install-system-unit is Linux-only (current platform: ${process.platform}). For local dev on macOS use \`agt manager install\`.`;
    if (json) jsonOutput({ ok: false, error: msg });
    else error(msg);
    process.exitCode = 1;
    return;
  }

  const env: Record<string, string> = {
    AGT_HOST: getHost(),
    HOME: user === 'root' ? '/root' : `/home/${user}`,
    USER: user,
  };
  const apiKey = getApiKey();
  if (apiKey) env.AGT_API_KEY = apiKey;
  // AGT_CLI_RELEASE_CHANNEL gates the self-update channel ('test' on the
  // test environment's hosts). Carry it through so the supervised manager's
  // self-update tracks the right npm dist-tag; unset ⇒ the manager defaults
  // to 'latest', unchanged for prod hosts.
  // ENG-7833: AGT_CLI_PIN_VERSION / AGT_CLI_PIN_ALLOW_URGENT pin the host to an
  // exact version, so carry them through too so both the manager's self-update and
  // the unit's ExecStartPre repair honor the pin.
  //
  // ENG-10696: AGT_ISOLATION (+ its two modifiers) decides whether agents on
  // this host run in containers at all. The bootstrap exports it before calling
  // us, and without it here that export is silently dropped — the unit would
  // start a manager with NO isolation intent on a host provisioned expressly to
  // have one, and nothing would report the gap.
  //
  // The unit body is also the durable place for it. `renderLinuxEnvFile`
  // rewrites /etc/agt/agt-manager.env with AGT_API_KEY alone, so hosts whose
  // isolation vars were hand-added to that file (agt-aws-1, integritylabs-2)
  // lose them on the next install; carried through here they survive one.
  //
  // AGT_ISOLATION_MEMORY is deliberately NOT carried: per-agent memory ceilings
  // come from `agent_class` since ENG-10641, and that variable is the host-wide
  // override that would flatten them back to a single number.
  applySupervisorPassthroughEnv(env);
  normalizePinInEnv(env);

  const result = await installSystemUnit({ agtBin, intervalSec, configDir, env, user });
  if (!result.ok) {
    if (json) jsonOutput({ ok: false, error: result.error });
    else error(result.error);
    process.exitCode = 1;
    return;
  }

  const status = systemUnitStatus();
  if (json) {
    jsonOutput({ ok: true, status, details: result.details });
    return;
  }
  success('System unit installed.');
  info(result.details);
  if (status.kind === 'installed' && status.pid != null) {
    info(`Manager running under systemd — PID ${status.pid}.`);
  }
}

export async function managerUninstallSystemUnitCommand(): Promise<void> {
  const json = isJsonMode();
  const { uninstallSystemUnit } = await import('../lib/manager-supervisor.js');

  const result = await uninstallSystemUnit();
  if (!result.ok) {
    if (json) jsonOutput({ ok: false, error: result.error });
    else error(result.error);
    process.exitCode = 1;
    return;
  }
  if (json) jsonOutput({ ok: true, details: result.details });
  else {
    success('System unit uninstalled.');
    info(result.details);
  }
}
